import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiRequest from 'helpers/apiRequest';

// Query Keys
const EMPTY_ARRAY: any[] = [];

export const BAR_QUERY_KEYS = {
  comptoirs: ['bar-comptoirs'] as const,
  comptoirStats: (id: number) => ['bar-comptoir-stats', id] as const,
  comptoirStock: (id: number) => ['bar-comptoir-stock', id] as const,
  lowStockItems: (id: number) => ['bar-low-stock', id] as const,
  lowStockAlerts: ['bar-low-stock-alerts'] as const,
  stockMovements: ['bar-stock-movements'] as const,
  recipes: ['bar-recipes'] as const,
  recipe: (id: number) => ['bar-recipe', id] as const,
  recipeProfitability: ['bar-recipe-profitability'] as const,
  popularRecipes: ['bar-popular-recipes'] as const,
  pourCostVariance: ['bar-pour-cost-variance'] as const,
  tabs: ['bar-tabs'] as const,
  tab: (id: number) => ['bar-tab', id] as const,
  menu: ['bar-menu'] as const,
  shifts: ['bar-shifts'] as const,
  bartenders: ['bar-bartenders'] as const,
  salesHistory: (filters: any) => ['bar-sales-history', filters] as const,
};

// ==================== TAB MANAGEMENT ====================

/**
 * Fetch bar tabs
 */
export const useFetchBarTabs = (status?: string, comptoirId?: number) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.tabs, status, comptoirId],
    refetchInterval: 15 * 1000,
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/tabs',
        method: 'GET',
        params: { status, comptoir_id: comptoirId },
      });
      return response;
    },
  });

  return {
    loadingTabs: isLoading,
    tabsData: data?.data?.data?.tabs ?? [],
    tabsResponse: data?.data?.message,
    tabsSuccess: isSuccess,
    tabsIsError: isError,
    refetchTabs: refetch,
  };
};

/**
 * Fetch bar sales history
 */
export const useFetchBarSalesHistory = (filters: {
  comptoir_id?: number;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: BAR_QUERY_KEYS.salesHistory(filters),
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/analytics/sales-history',
        method: 'GET',
        params: filters,
      });
      return response;
    },
  });

  return {
    loadingHistory: isLoading,
    historyData: data?.data?.data?.transactions ?? [],
    historyTotal: data?.data?.data?.total ?? 0,
    historySuccess: isSuccess,
    historyIsError: isError,
    refetchHistory: refetch,
  };
};

/**
 * Fetch single tab details
 */
export const useFetchTabDetails = (tabId: number, enabled = true) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: BAR_QUERY_KEYS.tab(tabId),
    refetchInterval: 15 * 1000,
    queryFn: async () => {
      const response = await apiRequest({
        url: `bar/tabs/${tabId}`,
        method: 'GET',
      });
      return response;
    },
    enabled: enabled && !!tabId,
  });

  return {
    loadingTabDetails: isLoading,
    tabDetailsData: data?.data?.data ?? data?.data,
    tabDetailsResponse: data?.data?.message,
    tabDetailsSuccess: isSuccess,
    tabDetailsIsError: isError,
    refetchTabDetails: refetch,
  };
};
export const useCreateBarTab = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } = useMutation({
    mutationFn: async (payload: {
      customer_name: string;
      customer_id?: number;
      customer_phone?: string;
      table_number?: string;
      table_id?: number;
      tab_name?: string;
      comptoir_id: number;
    }) => {
      const response = await apiRequest({
        url: 'bar/tabs',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
    },
  });

  return {
    loadingCreateTab: isPending,
    createTabData: data?.data ?? null,
    createTabResponse: data?.data?.message,
    createTabSuccess: isSuccess,
    createTabIsError: isError,
    requestCreateTab: mutate,
    requestCreateTabAsync: mutateAsync,
    clearCreateTab: reset,
  };
};

/**
 * Update a bar tab
 */
export const useUpdateBarTab = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, mutateAsync, isError, reset } = useMutation({
    mutationFn: async (payload: {
      tab_id: number;
      customer_name?: string;
      customer_id?: number;
      customer_phone?: string;
      tab_name?: string;
      table_id?: number | null;
      party_size?: number;
      comptoir_id?: number;
      discount_amount?: number;
      discount_reason?: string;
    }) => {
      const { tab_id, ...updateData } = payload;
      const response = await apiRequest({
        url: `bar/tabs/${tab_id}`,
        method: 'PUT',
        payload: updateData,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.tab(variables.tab_id),
      });
    },
  });

  return {
    loadingUpdateTab: isPending,
    updateTabSuccess: isSuccess,
    updateTabIsError: isError,
    requestUpdateTab: mutate,
    requestUpdateTabAsync: mutateAsync,
    clearUpdateTab: reset,
  };
};

/**
 * Delete a bar tab
 */
export const useDeleteBarTab = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (tabId: number) => {
      const response = await apiRequest({
        url: `bar/tabs/${tabId}`,
        method: 'DELETE',
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
    },
  });

  return {
    loadingDeleteTab: isPending,
    deleteTabSuccess: isSuccess,
    deleteTabIsError: isError,
    requestDeleteTab: mutate,
    clearDeleteTab: reset,
  };
};

/**
 * Add item to tab
 */
export const useAddTabItem = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (payload: {
        tab_id: number;
        menu_item_id: number;
        quantity: number;
        modifiers?: any[];
        is_recipe?: boolean;
        notes?: string;
      }) => {
        const { tab_id, ...itemData } = payload;
        const response = await apiRequest({
          url: `bar/tabs/${tab_id}/items`,
          method: 'POST',
          payload: itemData,
        });
        return response;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.tab(variables.tab_id),
        });
        queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
      },
    });

  return {
    loadingAddItem: isPending,
    addItemData: data?.data ?? null,
    addItemResponse: data?.data?.message,
    addItemSuccess: isSuccess,
    addItemIsError: isError,
    requestAddItem: mutate,
    requestAddItemAsync: mutateAsync,
    clearAddItem: reset,
  };
};

/**
 * Close a tab
 */
export const useCloseBarTab = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (payload: {
        tab_id: number;
        amount: number;
        payment_method: string;
        customer_id?: number;
        tip_amount?: number;
        debt_amount?: number;
        split_payments?: any[];
        branch_id?: number;
        customer_name?: string;
        customer_phone?: string;
        discount_amount?: number;
        discount_reason?: string;
      }) => {
        const { tab_id, ...closeData } = payload;
        const response = await apiRequest({
          url: `bar/tabs/${tab_id}/close`,
          method: 'POST',
          payload: closeData,
        });
        return response;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.tab(variables.tab_id),
        });
        queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
      },
    });

  return {
    loadingCloseTab: isPending,
    closeTabData: data?.data ?? null,
    closeTabResponse: data?.data?.message,
    closeTabSuccess: isSuccess,
    closeTabIsError: isError,
    requestCloseTab: mutate,
    requestCloseTabAsync: mutateAsync,
    clearCloseTab: reset,
  };
};

/**
 * Split a tab
 */
export const useSplitTab = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      tab_id: number;
      items_to_move: any[];
      destination_tab_id?: number;
      new_customer_name?: string;
    }) => {
      const { tab_id, ...splitData } = payload;
      const response = await apiRequest({
        url: `bar/tabs/${tab_id}/split`,
        method: 'POST',
        payload: splitData,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.tab(variables.tab_id),
      });
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
    },
  });

  return {
    loadingSplit: isPending,
    splitData: data?.data ?? null,
    splitSuccess: isSuccess,
    splitIsError: isError,
    requestSplit: mutate,
    clearSplit: reset,
  };
};

/**
 * Void an item from tab
 */
export const useVoidTabItem = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: { tab_id: number; item_id: number }) => {
      const { tab_id, item_id } = payload;
      const response = await apiRequest({
        url: `bar/tabs/${tab_id}/items/${item_id}`,
        method: 'DELETE',
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.tab(variables.tab_id),
      });
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
    },
  });

  return {
    loadingVoidItem: isPending,
    voidItemSuccess: isSuccess,
    voidItemIsError: isError,
    requestVoidItem: mutate,
    clearVoidItem: reset,
  };
};

/**
 * Update a tab item
 */
export const useUpdateTabItem = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      tab_id: number;
      item_id: number;
      quantity?: number;
      notes?: string;
    }) => {
      const { tab_id, item_id, ...updateData } = payload;
      const response = await apiRequest({
        url: `bar/tabs/${tab_id}/items/${item_id}`,
        method: 'PUT',
        payload: updateData,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.tab(variables.tab_id),
      });
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
    },
  });

  return {
    loadingUpdateTabItem: isPending,
    updateTabItemSuccess: isSuccess,
    updateTabItemIsError: isError,
    requestUpdateTabItem: mutate,
    clearUpdateTabItem: reset,
  };
};

/**
 * Clear all items from a tab
 */
export const useClearTabOrder = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (tab_id: number) => {
      const response = await apiRequest({
        url: `bar/tabs/${tab_id}/items`,
        method: 'DELETE',
      });
      return response;
    },
    onSuccess: (_, tab_id) => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tab(tab_id) });
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.tabs });
    },
  });

  return {
    loadingClearOrder: isPending,
    clearOrderSuccess: isSuccess,
    clearOrderIsError: isError,
    requestClearOrder: mutate,
    resetClearOrder: reset,
  };
};

/**
 * Fetch bar menu items
 */
export const useFetchBarMenu = (filters?: {
  is_active?: boolean;
  comptoir_id?: number;
  include_items?: boolean;
  include_unavailable?: boolean;
}) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.menu, filters],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/menus',
        method: 'GET',
        params: filters,
      });
      return response;
    },
  });

  return {
    loadingMenu: isLoading,
    menuData: data?.data?.data?.menus ?? [],
    menuResponse: data?.data?.message,
    menuSuccess: isSuccess,
    menuIsError: isError,
    refetchMenu: refetch,
  };
};

export const useFetchMenuDetails = (menuId: number, enabled = true) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.menu, menuId],
    queryFn: async () => {
      const response = await apiRequest({
        url: `bar/menus/${menuId}`,
        method: 'GET',
      });
      return response;
    },
    enabled: enabled && !!menuId,
  });

  return {
    loadingMenuDetails: isPending,
    menuDetailsData: data?.data?.data ?? data?.data,
    menuDetailsResponse: data?.data?.message,
    menuDetailsSuccess: isSuccess,
    menuDetailsIsError: isError,
    refetchMenuDetails: refetch,
  };
};

export const useCreateBarMenu = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      menu_name: string;
      description?: string;
      comptoir_id?: number;
      is_seasonal?: boolean;
      has_happy_hour?: boolean;
      happy_hour_start?: string;
      happy_hour_end?: string;
      happy_hour_discount_percentage?: number;
      happy_hour_days?: string[];
    }) => {
      const response = await apiRequest({
        url: 'bar/menus',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    loadingCreateMenu: isPending,
    createMenuData: data?.data ?? null,
    createMenuResponse: data?.data?.message,
    createMenuSuccess: isSuccess,
    createMenuIsError: isError,
    requestCreateMenu: mutate,
    clearCreateMenu: reset,
  };
};

export const useCreateMenuCategory = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      menu_id: number;
      category_name: string;
      description?: string;
      display_order?: number;
      kitchen_station_id?: number;
    }) => {
      const response = await apiRequest({
        url: 'bar/menus/categories',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...BAR_QUERY_KEYS.menu, variables.menu_id],
      });
    },
  });

  return {
    loadingCreateCategory: isPending,
    createCategoryData: data?.data?.data ?? data?.data ?? null,
    createCategoryResponse: data?.data?.message,
    createCategorySuccess: isSuccess,
    createCategoryIsError: isError,
    requestCreateCategory: mutate,
    clearCreateCategory: reset,
  };
};

export const useUpdateMenuCategory = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      category_id: number;
      category_name?: string;
      description?: string;
      display_order?: number;
      kitchen_station_id?: number;
      is_active?: boolean;
    }) => {
      const { category_id, ...updateData } = payload;
      const response = await apiRequest({
        url: `bar/menus/categories/${category_id}`,
        method: 'PUT',
        payload: updateData,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    loadingUpdateCategory: isPending,
    updateCategoryData: data?.data?.data ?? data?.data ?? null,
    updateCategoryResponse: data?.data?.message,
    updateCategorySuccess: isSuccess,
    updateCategoryIsError: isError,
    requestUpdateCategory: mutate,
    clearUpdateCategory: reset,
  };
};

export const useDeleteMenuCategory = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (categoryId: number) => {
      const response = await apiRequest({
        url: `bar/menus/categories/${categoryId}`,
        method: 'DELETE',
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    loadingDeleteCategory: isPending,
    deleteCategoryData: data?.data ?? null,
    deleteCategoryResponse: data?.data?.message,
    deleteCategorySuccess: isSuccess,
    deleteCategoryIsError: isError,
    requestDeleteCategory: mutate,
    clearDeleteCategory: reset,
  };
};

export const useCreateMenuItem = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      category_id: number;
      item_name: string;
      price: number;
      recipe_id?: number;
      product_id?: number;
      description?: string;
      happy_hour_price?: number;
      serving_size_ml?: number;
      is_alcoholic?: boolean;
      preparation_time?: number;
      kitchen_station_id?: number;
      ingredients?: string;
      allergens?: string;
      image_url?: string;
    }) => {
      const response = await apiRequest({
        url: 'bar/menus/items',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    loadingCreateItem: isPending,
    createItemData: data?.data ?? null,
    createItemResponse: data?.data?.message,
    createItemSuccess: isSuccess,
    createItemIsError: isError,
    requestCreateItem: mutate,
    clearCreateItem: reset,
  };
};

export const useUpdateMenuItem = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      item_id: number;
      item_name?: string;
      price?: number;
      description?: string;
      happy_hour_price?: number;
      serving_size_ml?: number;
      is_alcoholic?: boolean;
      is_featured?: boolean;
      is_available?: boolean;
      preparation_time?: number;
      kitchen_station_id?: number;
      ingredients?: string;
      allergens?: string;
      image_url?: string;
    }) => {
      const { item_id, ...updateData } = payload;
      const response = await apiRequest({
        url: `bar/menus/items/${item_id}`,
        method: 'PUT',
        payload: updateData,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    loadingUpdateItem: isPending,
    updateItemData: data?.data ?? null,
    updateItemResponse: data?.data?.message,
    updateItemSuccess: isSuccess,
    updateItemIsError: isError,
    requestUpdateItem: mutate,
    clearUpdateItem: reset,
  };
};

export const useDeleteMenuItem = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (itemId: number) => {
      const response = await apiRequest({
        url: `bar/menus/items/${itemId}`,
        method: 'DELETE',
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    loadingDeleteItem: isPending,
    deleteItemSuccess: isSuccess,
    deleteItemIsError: isError,
    requestDeleteItem: mutate,
    clearDeleteItem: reset,
  };
};

export const useUploadMenuItemImage = () => {
  const queryClient = useQueryClient();

  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: { id: number; file: File }) => {
      const formData = new FormData();
      formData.append('file', payload.file);

      const response = await apiRequest({
        url: `uploads/menu-item-image/${payload.id}`,
        method: 'POST',
        payload: formData,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.menu });
    },
  });

  return {
    uploadingImage: isPending,
    uploadSuccess: isSuccess,
    uploadError: isError,
    requestUploadImage: mutate,
    clearUpload: reset,
  };
};

export const useGenericUpload = () => {
  const { isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiRequest({
        url: 'uploads/generic-image',
        method: 'POST',
        payload: formData,
      });
      return response;
    },
  });

  return {
    uploading: isPending,
    uploadSuccess: isSuccess,
    requestUpload: mutate,
    clearUpload: reset,
  };
};

// ==================== COMPTOIR MANAGEMENT ====================

/**
 * Fetch all comptoirs with optional stats
 */
export const useFetchComptoirs = (includeStats = false) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.comptoirs, includeStats],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/comptoirs',
        method: 'GET',
        params: { include_stats: includeStats },
      });
      return response;
    },
  });

  return {
    loadingComptoirs: isLoading,
    comptoirsData: data?.data?.data?.comptoirs ?? [],
    comptoirsResponse: data?.data?.message,
    comptoirsSuccess: isSuccess,
    comptoirsIsError: isError,
    refetchComptoirs: refetch,
  };
};

/**
 * Fetch comptoirs assigned to current user (for bartender POS filtering)
 */
export const useFetchMyComptoirs = () => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['bar-my-comptoirs'],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/my-comptoirs',
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loadingMyComptoirs: isPending,
    myComptoirsData: data?.data?.data?.comptoirs ?? [],
    myComptoirsSuccess: isSuccess,
    myComptoirsIsError: isError,
    refetchMyComptoirs: refetch,
  };
};

/**
 * Create a new comptoir
 */
export const useCreateComptoir = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      comptoir_code: string;
      comptoir_name: string;
      location?: string;
      comptoir_type?: string;
      bartender_capacity?: number;
      is_active?: boolean;
      description?: string;
    }) => {
      const response = await apiRequest({
        url: 'bar/comptoirs',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate comptoirs list to refresh data
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.comptoirs });
    },
  });

  return {
    loadingCreateComptoir: isPending,
    createComptoirData: data?.data ?? null,
    createComptoirResponse: data?.data?.message,
    createComptoirSuccess: isSuccess,
    createComptoirIsError: isError,
    requestCreateComptoir: mutate,
    clearCreateComptoir: reset,
  };
};

/**
 * Update an existing comptoir
 */
export const useUpdateComptoir = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      comptoir_id: number;
      comptoir_code?: string;
      comptoir_name?: string;
      location?: string;
      comptoir_type?: string;
      bartender_capacity?: number;
      is_active?: boolean;
      description?: string;
    }) => {
      const { comptoir_id, ...updateData } = payload;
      const response = await apiRequest({
        url: `bar/comptoirs/${comptoir_id}`,
        method: 'PUT',
        payload: updateData,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.comptoirs });
    },
  });

  return {
    loadingUpdateComptoir: isPending,
    updateComptoirData: data?.data ?? null,
    updateComptoirResponse: data?.data?.message,
    updateComptoirSuccess: isSuccess,
    updateComptoirIsError: isError,
    requestUpdateComptoir: mutate,
    clearUpdateComptoir: reset,
  };
};

// ==================== BARTENDER ASSIGNMENT ====================

/**
 * Fetch bartenders assigned to a comptoir
 */
export const useFetchComptoirBartenders = (
  comptoirId: number,
  enabled = true
) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['bar-comptoir-bartenders', comptoirId],
    queryFn: async () => {
      const response = await apiRequest({
        url: `bar/comptoirs/${comptoirId}/bartenders`,
        method: 'GET',
      });
      return response;
    },
    enabled: enabled && !!comptoirId,
  });

  return {
    loadingBartenders: isPending,
    bartendersData: data?.data?.data?.bartenders ?? [],
    bartendersSuccess: isSuccess,
    bartendersIsError: isError,
    refetchBartenders: refetch,
  };
};

/**
 * Assign bartender to comptoir
 */
export const useAssignBartender = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: { comptoir_id: number; user_id: number }) => {
      const response = await apiRequest({
        url: `bar/comptoirs/${payload.comptoir_id}/bartenders`,
        method: 'POST',
        payload: { user_id: payload.user_id },
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bar-comptoir-bartenders', variables.comptoir_id],
      });
    },
  });

  return {
    loadingAssign: isPending,
    assignData: data?.data ?? null,
    assignSuccess: isSuccess,
    assignIsError: isError,
    requestAssign: mutate,
    clearAssign: reset,
  };
};

/**
 * Remove bartender from comptoir
 */
export const useRemoveBartender = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: { comptoir_id: number; user_id: number }) => {
      const response = await apiRequest({
        url: `bar/comptoirs/${payload.comptoir_id}/bartenders/${payload.user_id}`,
        method: 'DELETE',
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bar-comptoir-bartenders', variables.comptoir_id],
      });
    },
  });

  return {
    loadingRemove: isPending,
    removeData: data?.data ?? null,
    removeSuccess: isSuccess,
    removeIsError: isError,
    requestRemove: mutate,
    clearRemove: reset,
  };
};

/**
 * Fetch available users who can be assigned as bartenders
 */
export const useFetchAvailableBartenders = () => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['bar-available-bartenders'],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/bartenders/available',
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loadingAvailable: isPending,
    availableData: data?.data?.data?.users ?? [],
    availableSuccess: isSuccess,
    availableIsError: isError,
    refetchAvailable: refetch,
  };
};

// ==================== STOCK MANAGEMENT ====================

/**
 * Fetch comptoir stock
 */
export const useComptoirStock = (
  comptoirId: number,
  lowStockOnly = false,
  enabled = true
) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.comptoirStock(comptoirId), lowStockOnly],
    queryFn: async () => {
      const response = await apiRequest({
        url: `bar/comptoir/${comptoirId}/stock`,
        method: 'GET',
        params: { low_stock_only: lowStockOnly },
      });
      return response;
    },
    enabled: enabled && !!comptoirId,
  });

  return {
    loadingStock: isLoading,
    stockData: data?.data?.data?.stock ?? [],
    stockResponse: data?.data?.message,
    stockSuccess: isSuccess,
    stockIsError: isError,
    refetchStock: refetch,
  };
};

/**
 * Restock a comptoir
 */
export const useRestockComptoir = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      comptoir_id: number;
      product_id: number;
      quantity: number;
      reason?: string;
    }) => {
      const response = await apiRequest({
        url: 'bar/restock',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate stock for the specific comptoir
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.comptoirStock(variables.comptoir_id),
      });
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.comptoirs });
    },
  });

  return {
    loadingRestock: isPending,
    restockData: data?.data ?? null,
    restockResponse: data?.data?.message,
    restockSuccess: isSuccess,
    restockIsError: isError,
    requestRestock: mutate,
    clearRestock: reset,
  };
};

/**
 * Transfer stock between comptoirs
 */
export const useTransferBetweenComptoirs = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      from_comptoir_id: number;
      to_comptoir_id: number;
      product_id: number;
      quantity: number;
      reason?: string;
    }) => {
      const response = await apiRequest({
        url: 'bar/transfer',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate stock for both comptoirs
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.comptoirStock(variables.from_comptoir_id),
      });
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.comptoirStock(variables.to_comptoir_id),
      });
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.comptoirs });
    },
  });

  return {
    loadingTransfer: isPending,
    transferData: data?.data ?? null,
    transferResponse: data?.data?.message,
    transferSuccess: isSuccess,
    transferIsError: isError,
    requestTransfer: mutate,
    clearTransfer: reset,
  };
};

/**
 * Report wastage at a comptoir
 */
export const useReportWastage = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      comptoir_id: number;
      product_id: number;
      quantity: number;
      reason: string;
      notes?: string;
    }) => {
      const response = await apiRequest({
        url: 'bar/wastage',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate stock for the specific comptoir
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.comptoirStock(variables.comptoir_id),
      });
      // Also invalidate stock movements history
      queryClient.invalidateQueries({
        queryKey: BAR_QUERY_KEYS.stockMovements,
      });
    },
  });

  return {
    loadingWastage: isPending,
    wastageData: data?.data ?? null,
    wastageResponse: data?.data?.message,
    wastageSuccess: isSuccess,
    wastageIsError: isError,
    requestReportWastage: mutate,
    clearWastage: reset,
  };
};

// ==================== SERVING / POS OPERATIONS ====================

/**
 * Serve glass (shot or glass serving)
 */
export const useServeGlass = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (payload: {
        comptoir_id: number;
        product_id: number;
        quantity: number;
        serving_size_ml?: number;
        recorded_by: string;
        customer_id?: number;
        customer_name?: string;
        customer_phone?: string;
        payment_method?: string;
        debt_amount?: number;
        branch_id?: number;
      }) => {
        const response = await apiRequest({
          url: 'bar/servings/serve-glass',
          method: 'POST',
          payload,
        });
        return response;
      },
      onSuccess: (_, variables) => {
        // Invalidate stock for the comptoir
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.comptoirStock(variables.comptoir_id),
        });
      },
    });

  return {
    loadingServeGlass: isPending,
    serveGlassData: data?.data ?? null,
    serveGlassResponse: data?.data?.message,
    serveGlassSuccess: isSuccess,
    serveGlassIsError: isError,
    requestServeGlass: mutate,
    requestServeGlassAsync: mutateAsync,
    clearServeGlass: reset,
  };
};

/**
 * Serve bottle (full bottle service)
 */
export const useServeBottle = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (payload: {
        comptoir_id: number;
        product_id: number;
        quantity: number;
        recorded_by: string;
        customer_id?: number;
        customer_name?: string;
        customer_phone?: string;
        payment_method?: string;
        debt_amount?: number;
        branch_id?: number;
      }) => {
        const response = await apiRequest({
          url: 'bar/servings/serve-bottle',
          method: 'POST',
          payload,
        });
        return response;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.comptoirStock(variables.comptoir_id),
        });
      },
    });

  return {
    loadingServeBottle: isPending,
    serveBottleData: data?.data ?? null,
    serveBottleResponse: data?.data?.message,
    serveBottleSuccess: isSuccess,
    serveBottleIsError: isError,
    requestServeBottle: mutate,
    requestServeBottleAsync: mutateAsync,
    clearServeBottle: reset,
  };
};

/**
 * Serve recipe (cocktail)
 */
export const useServeRecipe = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (payload: {
        comptoir_id: number;
        recipe_id: number;
        quantity: number;
        recorded_by: string;
        customer_id?: number;
        customer_name?: string;
        customer_phone?: string;
        payment_method?: string;
        debt_amount?: number;
        branch_id?: number;
      }) => {
        const response = await apiRequest({
          url: 'bar/servings/serve-recipe',
          method: 'POST',
          payload,
        });
        return response;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.comptoirStock(variables.comptoir_id),
        });
      },
    });

  return {
    loadingServeRecipe: isPending,
    serveRecipeData: data?.data ?? null,
    serveRecipeResponse: data?.data?.message,
    serveRecipeSuccess: isSuccess,
    serveRecipeIsError: isError,
    requestServeRecipe: mutate,
    requestServeRecipeAsync: mutateAsync,
    clearServeRecipe: reset,
  };
};

// ==================== ALERTS & MOVEMENTS ====================

/**
 * Get low stock alerts
 */
export const useLowStockAlerts = (comptoirId?: number) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.lowStockAlerts, comptoirId],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/low-stock-alerts',
        method: 'GET',
        params: comptoirId ? { comptoir_id: comptoirId } : undefined,
      });
      return response;
    },
  });

  return {
    loadingAlerts: isPending,
    alertsData: data?.data?.alerts ?? [],
    alertsResponse: data?.data?.message,
    alertsSuccess: isSuccess,
    alertsIsError: isError,
    refetchAlerts: refetch,
  };
};

/**
 * Get stock movements
 */
export const useStockMovements = (filters?: {
  comptoir_id?: number;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.stockMovements, filters],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/stock-movements',
        method: 'GET',
        params: filters,
      });
      return response;
    },
  });

  return {
    loadingMovements: isPending,
    movementsData: data?.data?.data?.movements ?? [],
    movementsMeta: data?.data?.data?.meta,
    movementsResponse: data?.data?.message,
    movementsSuccess: isSuccess,
    movementsIsError: isError,
    refetchMovements: refetch,
  };
};

// ==================== RECIPE MANAGEMENT ====================

/**
 * Fetch recipes
 */
export const useFetchRecipes = (filters?: {
  category?: string;
  is_active?: boolean;
  search?: string;
}) => {
  const { data, isLoading, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.recipes, filters],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/recipes',
        method: 'GET',
        params: filters,
      });
      return response;
    },
  });

  return {
    loadingRecipes: isLoading,
    recipesData: data?.data?.data?.recipes ?? EMPTY_ARRAY,
    recipesResponse: data?.data?.message,
    recipesSuccess: isSuccess,
    recipesIsError: isError,
    refetchRecipes: refetch,
  };
};

/**
 * Create a new recipe
 */
export const useCreateRecipe = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, error, reset } = useMutation({
    mutationFn: async (payload: {
      name: string;
      selling_price: number;
      category?: string;
      difficulty?: string;
      glassware?: string;
      garnish?: string;
      ice?: string;
      instructions?: string;
      preparation_time_seconds?: number;
      has_happy_hour_price?: boolean;
      happy_hour_price?: number;
      ingredients: {
        product_id: number;
        amount_ml: number;
        is_optional?: boolean;
        notes?: string;
      }[];
      image_url?: string;
      allergens?: string;
    }) => {
      const response = await apiRequest({
        url: 'bar/recipes',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.recipes });
    },
  });

  return {
    loadingCreateRecipe: isPending,
    createRecipeData: data?.data ?? null,
    createRecipeResponse: data?.data?.message,
    createRecipeSuccess: isSuccess,
    createRecipeIsError: isError,
    createRecipeError: error,
    requestCreateRecipe: mutate,
    clearCreateRecipe: reset,
  };
};

/**
 * Get recipe by ID
 */
export const useRecipeById = (recipeId: number, enabled = true) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: BAR_QUERY_KEYS.recipe(recipeId),
    queryFn: async () => {
      const response = await apiRequest({
        url: `bar/recipes/${recipeId}`,
        method: 'GET',
      });
      return response;
    },
    enabled: enabled && !!recipeId,
  });

  return {
    loadingRecipe: isPending,
    recipeData: data?.data?.data ?? null,
    recipeResponse: data?.data?.message,
    recipeSuccess: isSuccess,
    recipeIsError: isError,
    refetchRecipe: refetch,
  };
};

/**
 * Create recipe
 */

/**
 * Update recipe
 */
export const useUpdateRecipe = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, error, reset } =
    useMutation({
      mutationFn: async (payload: { recipe_id: number; data: any }) => {
        const { recipe_id, data } = payload;
        const response = await apiRequest({
          url: `bar/recipes/${recipe_id}`,
          method: 'PUT',
          payload: data,
        });
        return response;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.recipes });
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.recipe(variables.recipe_id),
        });
      },
    });

  return {
    loadingUpdateRecipe: isPending,
    updateRecipeData: data?.data ?? null,
    updateRecipeResponse: data?.data?.message,
    updateRecipeSuccess: isSuccess,
    updateRecipeIsError: isError,
    updateRecipeError: error,
    requestUpdateRecipe: mutate,
    requestUpdateRecipeAsync: mutateAsync,
    clearUpdateRecipe: reset,
  };
};

/**
 * Recalculate recipe cost
 */
export const useRecalculateRecipeCost = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (recipeId: number) => {
        const response = await apiRequest({
          url: `bar/recipes/${recipeId}/recalculate-cost`,
          method: 'PUT',
        });
        return response;
      },
      onSuccess: (_, recipeId) => {
        queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.recipes });
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.recipe(recipeId),
        });
      },
    });

  return {
    loadingRecalculate: isPending,
    recalculateData: data?.data ?? null,
    recalculateResponse: data?.data?.message,
    recalculateSuccess: isSuccess,
    recalculateIsError: isError,
    requestRecalculate: mutate,
    requestRecalculateAsync: mutateAsync,
    clearRecalculate: reset,
  };
};

// ==================== ANALYTICS ====================

/**
 * Get recipe profitability
 */
export const useRecipeProfitability = () => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: BAR_QUERY_KEYS.recipeProfitability,
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/recipes/analytics/profitability',
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loadingProfitability: isPending,
    profitabilityData: data?.data ?? [],
    profitabilityResponse: data?.data?.message,
    profitabilitySuccess: isSuccess,
    profitabilityIsError: isError,
    refetchProfitability: refetch,
  };
};

/**
 * Get popular recipes
 */
export const usePopularRecipes = (filters?: {
  from_date?: string;
  to_date?: string;
  limit?: number;
}) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.popularRecipes, filters],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/recipes/analytics/popular',
        method: 'GET',
        params: filters,
      });
      return response;
    },
  });

  return {
    loadingPopular: isPending,
    popularData: data?.data ?? [],
    popularResponse: data?.data?.message,
    popularSuccess: isSuccess,
    popularIsError: isError,
    refetchPopular: refetch,
  };
};

/**
 * Calculate pour cost variance
 */
export const useCalculatePourCostVariance = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, mutateAsync, isError, reset } =
    useMutation({
      mutationFn: async (payload: {
        comptoir_id: number;
        from_date: string;
        to_date: string;
      }) => {
        const response = await apiRequest({
          url: 'bar/pour-cost/variance/calculate',
          method: 'POST',
          payload,
        });
        return response;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: BAR_QUERY_KEYS.pourCostVariance,
        });
      },
    });

  return {
    loadingCalculateVariance: isPending,
    calculateVarianceData: data?.data ?? null,
    calculateVarianceResponse: data?.data?.message,
    calculateVarianceSuccess: isSuccess,
    calculateVarianceIsError: isError,
    requestCalculateVariance: mutate,
    requestCalculateVarianceAsync: mutateAsync,
    clearCalculateVariance: reset,
  };
};

/**
 * Get pour cost variance
 */
export const usePourCostVariance = (filters?: {
  comptoir_id?: number;
  from_date?: string;
  to_date?: string;
}) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.pourCostVariance, filters],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/pour-cost/variance',
        method: 'GET',
        params: filters,
      });
      return response;
    },
  });

  return {
    loadingVariance: isPending,
    varianceData: data?.data ?? [],
    varianceResponse: data?.data?.message,
    varianceSuccess: isSuccess,
    varianceIsError: isError,
    refetchVariance: refetch,
  };
};

// ==================== SHIFT MANAGEMENT ====================

/**
 * Fetch shifts
 */
export const useFetchShifts = (status?: string) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: [...BAR_QUERY_KEYS.shifts, status],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'bar/shifts',
        method: 'GET',
        params: { status },
      });
      return response;
    },
  });

  return {
    loadingShifts: isPending,
    shiftsData: data?.data?.data?.shifts ?? [],
    shiftsResponse: data?.data?.message,
    shiftsSuccess: isSuccess,
    shiftsIsError: isError,
    refetchShifts: refetch,
  };
};

/**
 * Create a new shift
 */
export const useCreateShift = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      shift_date: string;
      shift_type: string;
      scheduled_start: string;
      scheduled_end: string;
      lead_bartender_id: number;
      comptoir_id: number;
      opening_cash_float: number;
    }) => {
      const response = await apiRequest({
        url: 'bar/shifts',
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.shifts });
    },
  });

  return {
    loadingCreateShift: isPending,
    createShiftData: data?.data ?? null,
    createShiftResponse: data?.data?.message,
    createShiftSuccess: isSuccess,
    createShiftIsError: isError,
    requestCreateShift: mutate,
    clearCreateShift: reset,
  };
};

/**
 * Close a shift
 */
export const useCloseShift = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutate, isError, reset } = useMutation({
    mutationFn: async (payload: {
      shift_id: number;
      closing_cash_count: number;
      tips_collected: number;
      notes?: string;
    }) => {
      const { shift_id, ...closeData } = payload;
      const response = await apiRequest({
        url: `bar/shifts/${shift_id}/close`,
        method: 'POST',
        payload: closeData,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BAR_QUERY_KEYS.shifts });
    },
  });

  return {
    loadingCloseShift: isPending,
    closeShiftData: data?.data ?? null,
    closeShiftResponse: data?.data?.message,
    closeShiftSuccess: isSuccess,
    closeShiftIsError: isError,
    requestCloseShift: mutate,
    clearCloseShift: reset,
  };
};

/**
 * Fetch bartenders
 */
export const useFetchBartenders = () => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: BAR_QUERY_KEYS.bartenders,
    queryFn: async () => {
      // Using hardcoded endpoint as per original file, or assuming standard path
      const response = await apiRequest({
        url: 'bar/bartenders',
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loadingBartenders: isPending,
    bartendersData: data?.data?.data?.bartenders ?? [],
    bartendersResponse: data?.data?.message,
    bartendersSuccess: isSuccess,
    bartendersIsError: isError,
    refetchBartenders: refetch,
  };
};
